import { driverListQuerySchema } from '@chukua/common';
import { Select } from 'antd';
import { useCallback, useEffect } from 'react';
import { useRemoteSearchOptions } from '../../../shared/hooks/use-remote-search-options';
import { getDriver, listDrivers } from '../drivers.api';

const PAGE_SIZE = 20;

interface DriverSearchSelectProps {
  id?: string;
  value?: string;
  onChange?: (value: string | undefined) => void;
  disabled?: boolean;
  /** For a filter, where choosing nobody is a valid answer: clearing calls `onChange(undefined)`. */
  allowClear?: boolean;
}

/** Remote-search driver picker: `GET /drivers?q=` by name, opening on the first page. A saved `value` is fetched so the name shows rather than the id. */
export function DriverSearchSelect({ id, value, onChange, disabled, allowClear }: DriverSearchSelectProps) {
  const fetchDrivers = useCallback(
    (q: string | undefined) =>
      listDrivers(driverListQuerySchema.parse({ q, pageSize: PAGE_SIZE })).then((page) =>
        page.items.map((driver) => ({ value: driver.id, label: driver.name })),
      ),
    [],
  );
  const { pin, selectProps } = useRemoteSearchOptions(fetchDrivers);

  useEffect(() => {
    if (!value) return;
    getDriver(value).then((driver) => pin({ value: driver.id, label: driver.name }));
  }, [value, pin]);

  return (
    <Select
      {...selectProps}
      id={id}
      value={value}
      onChange={onChange}
      disabled={disabled}
      allowClear={allowClear}
      placeholder="Select or search drivers"
      notFoundContent={selectProps.loading ? 'Searching…' : 'No drivers found'}
    />
  );
}
